import React, { useEffect, useMemo, useState } from 'react';
import DashboardPagination from '../components/DashboardPagination';
import api from '../services/api';
import { UI_THEME } from '../ui/theme';
import { notifyError, notifySuccess } from '../utils/notify';

interface TipoDocumentoConfig {
  id: string;
  codigo: string;
  nome: string;
  categoria: string;
  descricao?: string;
  ativo: boolean;
  obrigatorio: boolean;
  numeroDocumentoObrigatorio: boolean;
  dataEmissaoObrigatoria: boolean;
  dataValidadeObrigatoria: boolean;
  ordem: number;
  formatosAceitos?: string;
  tamanhoMaximoMb?: number;
}

const normalizeName = (value?: string) =>
  (value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const TiposDocumentoConfigPage: React.FC = () => {
  const [configs, setConfigs] = useState<TipoDocumentoConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<TipoDocumentoConfig | null>(null);
  const [filterCategoria, setFilterCategoria] = useState<string>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const isSaas = UI_THEME === 'saas';
  const surfaceClass = 'border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900/80';
  const heroClass = isSaas
    ? 'border-teal-100 bg-gradient-to-br from-white via-teal-50/70 to-indigo-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'
    : 'border-slate-200 bg-gradient-to-br from-white via-slate-50 to-blue-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800';
  const accentTextClass = isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-blue-700 dark:text-blue-300';
  const bodyTextClass = 'text-slate-600 dark:text-slate-300';
  const labelTextClass = 'text-slate-500 dark:text-slate-400';
  const strongTextClass = 'text-slate-900 dark:text-slate-100';
  const inputClass = 'w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-blue-900/40';
  const primaryButtonClass = isSaas
    ? 'bg-teal-600 text-white hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-400'
    : 'bg-blue-700 text-white hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-500';
  const secondaryButtonClass = 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800';
  const checkboxClass = isSaas
    ? 'h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500 disabled:cursor-not-allowed disabled:opacity-60'
    : 'h-4 w-4 rounded border-slate-300 text-blue-700 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60';

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/tipos-documento-config');
      const data: TipoDocumentoConfig[] = response.data || [];
      const targetName = 'declaracao de utilidade publica municipal';

      for (const cfg of data) {
        if (normalizeName(cfg.nome) === targetName && !cfg.obrigatorio) {
          try {
            const updated = { ...cfg, obrigatorio: true };
            await api.put(`/tipos-documento-config/${cfg.codigo}`, updated).catch(() => null);
            cfg.obrigatorio = true;
          } catch {
            cfg.obrigatorio = true;
          }
        }
      }

      setConfigs(data);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      notifyError('Erro ao carregar configurações de documentos');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (config: TipoDocumentoConfig) => {
    setEditingId(config.id);
    setEditForm({
      id: config.id,
      codigo: config.codigo,
      nome: config.nome,
      categoria: config.categoria,
      descricao: config.descricao,
      ativo: config.ativo,
      obrigatorio: config.obrigatorio !== undefined ? config.obrigatorio : true,
      numeroDocumentoObrigatorio: config.numeroDocumentoObrigatorio,
      dataEmissaoObrigatoria: config.dataEmissaoObrigatoria,
      dataValidadeObrigatoria: config.dataValidadeObrigatoria,
      ordem: config.ordem,
      formatosAceitos: config.formatosAceitos ?? '.pdf,.jpg,.jpeg,.png',
      tamanhoMaximoMb: config.tamanhoMaximoMb ?? 5,
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm(null);
  };

  const handleSave = async () => {
    if (!editForm) return;

    if (!editForm.obrigatorio) {
      editForm.obrigatorio = true;
    }

    try {
      await api.put(`/tipos-documento-config/${editForm.codigo}`, editForm);
      notifySuccess('Configuração atualizada com sucesso!');
      setEditingId(null);
      setEditForm(null);
      await fetchConfigs();
    } catch (error: any) {
      console.error('Erro ao atualizar configuração:', error);
      notifyError(error.response?.data?.error || 'Erro ao atualizar configuração');
    }
  };

  const handleCheckboxChange = (field: keyof TipoDocumentoConfig) => {
    if (!editForm) return;

    setEditForm((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        [field]: !prev[field],
      };
    });
  };

  const handleCategoriaChange = (categoria: string) => {
    setFilterCategoria(categoria);
    setPage(1);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(1);
  };

  const categorias = useMemo(
    () => Array.from(new Set(configs.map((config) => config.categoria))).filter(Boolean).sort(),
    [configs],
  );

  const filteredConfigs = useMemo(
    () => filterCategoria ? configs.filter((config) => config.categoria === filterCategoria) : configs,
    [configs, filterCategoria],
  );

  const paginatedConfigs = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredConfigs.slice(start, start + pageSize);
  }, [filteredConfigs, page, pageSize]);

  const activeConfigs = configs.filter((config) => config.ativo).length;
  const requiredConfigs = configs.filter((config) => config.obrigatorio).length;
  const withValidityDate = configs.filter((config) => config.dataValidadeObrigatoria).length;
  const summaryCards = [
    { label: 'Tipos cadastrados', value: configs.length, dot: 'bg-slate-400' },
    { label: 'Ativos', value: activeConfigs, dot: isSaas ? 'bg-teal-500' : 'bg-blue-600' },
    { label: 'Obrigatórios', value: requiredConfigs, dot: 'bg-emerald-500' },
    { label: 'Exigem validade', value: withValidityDate, dot: 'bg-amber-500' },
  ];

  const renderYesNo = (value: boolean) => (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
      value
        ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/30'
        : 'bg-slate-100 text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700'
    }`}>
      {value ? 'Sim' : 'Não'}
    </span>
  );

  return (
    <div className="w-full space-y-6">
      <section className={`rounded-3xl border ${heroClass}`}>
        <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${accentTextClass}`}>
          {isSaas ? 'Configurações' : 'Administrador'}
        </p>
        <h1 className={`mt-2 text-2xl font-bold tracking-tight sm:text-3xl ${strongTextClass}`}>
          Tipos de documentos
        </h1>
        <p className={`mt-2 text-sm leading-6 ${bodyTextClass}`}>
          Defina os campos exigidos em cada documento usado nos cadastros, análises e prestações de contas.
        </p>

        <div className={`mt-5 overflow-hidden rounded-2xl border ${surfaceClass}`}>
          <div className="grid divide-y divide-slate-200 dark:divide-slate-700 sm:grid-cols-2 sm:divide-x sm:divide-y-0 xl:grid-cols-4">
            {summaryCards.map((card) => (
              <div key={card.label} className="min-h-[72px] px-4 py-3">
                <p className={`flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] ${labelTextClass}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${card.dot}`} />
                  {card.label}
                </p>
                <p className={`mt-2 text-2xl font-bold ${strongTextClass}`}>{card.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={`rounded-3xl border p-4 ${surfaceClass}`}>
        <div className="grid gap-3 md:grid-cols-[minmax(220px,320px)_auto] md:items-end">
          <div>
            <label className={`mb-1 block text-xs font-semibold uppercase tracking-[0.12em] ${labelTextClass}`}>
              Categoria
            </label>
            <select
              value={filterCategoria}
              onChange={(e) => handleCategoriaChange(e.target.value)}
              className={inputClass}
            >
              <option value="">Todas as categorias</option>
              {categorias.map((categoria) => (
                <option key={categoria} value={categoria}>
                  {categoria}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => handleCategoriaChange('')}
              className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${secondaryButtonClass}`}
            >
              Limpar filtros
            </button>
          </div>
        </div>
      </section>

      <section className={`overflow-hidden rounded-3xl border ${surfaceClass}`}>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
            <thead className="bg-slate-50 dark:bg-slate-800/80">
              <tr>
                <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.12em] ${labelTextClass}`}>Documento</th>
                <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.12em] ${labelTextClass}`}>Categoria</th>
                <th className={`px-4 py-3 text-center text-xs font-semibold uppercase tracking-[0.12em] ${labelTextClass}`}>Obrigatório</th>
                <th className={`px-4 py-3 text-center text-xs font-semibold uppercase tracking-[0.12em] ${labelTextClass}`}>Nº doc.</th>
                <th className={`px-4 py-3 text-center text-xs font-semibold uppercase tracking-[0.12em] ${labelTextClass}`}>Emissão</th>
                <th className={`px-4 py-3 text-center text-xs font-semibold uppercase tracking-[0.12em] ${labelTextClass}`}>Validade</th>
                <th className={`px-4 py-3 text-center text-xs font-semibold uppercase tracking-[0.12em] ${labelTextClass}`}>Situação</th>
                <th className={`px-4 py-3 text-right text-xs font-semibold uppercase tracking-[0.12em] ${labelTextClass}`}>Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white dark:divide-slate-800 dark:bg-slate-900">
              {loading ? (
                <tr>
                  <td colSpan={8} className={`px-6 py-10 text-center text-sm ${labelTextClass}`}>
                    Carregando configurações...
                  </td>
                </tr>
              ) : paginatedConfigs.length === 0 ? (
                <tr>
                  <td colSpan={8} className={`px-6 py-10 text-center text-sm ${labelTextClass}`}>
                    Nenhuma configuração encontrada.
                  </td>
                </tr>
              ) : (
                paginatedConfigs.map((config) => {
                  const editing = editingId === config.id;

                  return (
                    <tr
                      key={config.id || config.codigo}
                      className={`${editing ? 'bg-blue-50/80 dark:bg-blue-500/10' : 'hover:bg-slate-50 dark:hover:bg-slate-800/60'}`}
                    >
                      <td className="px-4 py-4">
                        <div className={`max-w-xl text-sm font-semibold ${strongTextClass}`}>
                          {config.nome}
                          {editing && (
                            <span className={`ml-2 rounded-full px-2 py-0.5 text-[11px] font-semibold ${isSaas ? 'bg-teal-100 text-teal-700 dark:bg-teal-500/15 dark:text-teal-300' : 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300'}`}>
                              Editando
                            </span>
                          )}
                        </div>
                        {config.descricao && (
                          <p className={`mt-1 line-clamp-2 max-w-2xl text-xs ${bodyTextClass}`}>{config.descricao}</p>
                        )}
                        <p className={`mt-1 truncate text-[11px] ${labelTextClass}`}>
                          Código: {config.codigo}
                        </p>
                      </td>
                      <td className={`px-4 py-4 text-sm ${bodyTextClass}`}>{config.categoria}</td>
                      <td className="px-4 py-4 text-center">
                        {editing ? (
                          <input
                            type="checkbox"
                            checked
                            disabled
                            aria-label="Obrigatório"
                            className={checkboxClass}
                          />
                        ) : renderYesNo(config.obrigatorio)}
                      </td>
                      <td className="px-4 py-4 text-center">
                        {editing ? (
                          <input
                            type="checkbox"
                            checked={editForm?.numeroDocumentoObrigatorio || false}
                            onChange={() => handleCheckboxChange('numeroDocumentoObrigatorio')}
                            className={checkboxClass}
                          />
                        ) : renderYesNo(config.numeroDocumentoObrigatorio)}
                      </td>
                      <td className="px-4 py-4 text-center">
                        {editing ? (
                          <input
                            type="checkbox"
                            checked={editForm?.dataEmissaoObrigatoria || false}
                            onChange={() => handleCheckboxChange('dataEmissaoObrigatoria')}
                            className={checkboxClass}
                          />
                        ) : renderYesNo(config.dataEmissaoObrigatoria)}
                      </td>
                      <td className="px-4 py-4 text-center">
                        {editing ? (
                          <input
                            type="checkbox"
                            checked={editForm?.dataValidadeObrigatoria || false}
                            onChange={() => handleCheckboxChange('dataValidadeObrigatoria')}
                            className={checkboxClass}
                          />
                        ) : renderYesNo(config.dataValidadeObrigatoria)}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                          config.ativo
                            ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/30'
                            : 'bg-red-50 text-red-700 ring-1 ring-red-200 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/30'
                        }`}>
                          {config.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right text-sm">
                        {editing ? (
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={handleSave}
                              className={`rounded-xl px-3 py-2 text-sm font-semibold transition ${primaryButtonClass}`}
                            >
                              Salvar
                            </button>
                            <button
                              type="button"
                              onClick={handleCancelEdit}
                              className={`rounded-xl px-3 py-2 text-sm font-medium transition ${secondaryButtonClass}`}
                            >
                              Cancelar
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleEdit(config)}
                            className={`font-semibold ${accentTextClass}`}
                          >
                            Editar
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <DashboardPagination
          page={page}
          pageSize={pageSize}
          totalItems={filteredConfigs.length}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={[10, 20, 50]}
          itemLabel="tipos"
        />
      </section>
    </div>
  );
};

export default TiposDocumentoConfigPage;
